import { css, keyframes } from "@emotion/css";
import { ToastPosition } from "react-hot-toast";

import { blue, green, red, white, yellow } from "@/modules/theme/utils/colors";
import { Theme } from "@/modules/theme/utils/createTheme";

import { TOAST_POSITION } from "../../Toaster";

import closeButtonIconSrc from "./close-button-icon.svg";
import errorIconSrc from "./error-icon.svg";
import infoIconSrc from "./info-icon.svg";
import successIconSrc from "./success-icon.svg";
import warningIconSrc from "./warning-icon.svg";

export const createAlertToastStyles = (
  { spacing, boxShadows, borderRadius, typography, palette }: Theme,
  position?: ToastPosition
) => ({
  basic: css({
    padding: spacing(1),
    minWidth: 300,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    boxShadow: boxShadows.snackbar,
    borderRadius: borderRadius.default,
    fontSize: typography.pxToRem(11),
    color: palette.default.text,

    "&:before": {
      content: "''",
      width: 22,
      height: 22,
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
      marginRight: spacing(1),
      fontSize: typography.pxToRem(12),
    },
  }),
  contentWrapper: css({
    flex: 1,
  }),
  loading: css({
    backgroundColor: white,
  }),
  success: css({
    backgroundColor: green[100],

    "&:before": {
      backgroundImage: `url(${successIconSrc})`,
    },
  }),
  error: css({
    backgroundColor: red[100],
    "&:before": {
      backgroundImage: `url(${errorIconSrc})`,
    },
  }),
  warning: css({
    backgroundColor: yellow[200],
    "&:before": {
      backgroundImage: `url(${warningIconSrc})`,
    },
  }),
  info: css({
    backgroundColor: blue[100],
    "&:before": {
      backgroundImage: `url(${infoIconSrc})`,
    },
  }),
  enter: css({
    animation: `${createEnterAnimationKeyframes(position)} 200ms ease-out`,
  }),
  leave: css({
    animation: `${leaveAnimationKeyframes} 150ms ease-in forwards`,
  }),
  closeButton: css({
    borderRadius: borderRadius.round,
    appearance: "none",
    border: "none",
    width: 22,
    height: 22,
    background: `url(${closeButtonIconSrc}) center/contain no-repeat`,
    cursor: "pointer",
    marginLeft: spacing(1),

    "&:hover": {
      opacity: 0.8,
    },
  }),
});

const createEnterAnimationKeyframes = (position?: ToastPosition) => {
  const appliedPosition = position ?? TOAST_POSITION;
  const startTranslateYValue = appliedPosition?.includes("bottom")
    ? "100%"
    : "-100%";
  return keyframes({
    "0%": {
      transform: `scale(0.9) translateY(${startTranslateYValue})`,
      opacity: 0,
    },
    "100%": {
      transform: `scale(1) translateY(0)`,
      opacity: 1,
    },
  });
};

const leaveAnimationKeyframes = keyframes({
  "0%": { transform: "scale(1)", opacity: 1 },
  "100%": { transform: "scale(0.9)", opacity: 0 },
});
